import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Upload, X, FileText, DollarSign, Database, Loader2, CreditCard, AlertTriangle, RefreshCw, HelpCircle, MessageSquare } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import UnifiedDocumentUploader from "@/components/UnifiedDocumentUploader";
import RevampedChronologyTab from "@/components/RevampedChronologyTab";
import StructuredDataTab from "@/components/StructuredDataTab";
import SpecialsTab from "@/components/SpecialsTab";
import BuyCreditsModal from "@/components/BuyCreditsModal";
import ProcessingModal from "@/components/ProcessingModal";
import { useJobPolling } from "@/hooks/useJobPolling";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to check if results have valid data (supports both old and new schema)
const hasValidResultData = (results) => {
  if (!results || !results.result_data) return false;
  const rd = results.result_data;
  
  // NEW SCHEMA checks
  const hasPatientDemo = rd.patient_demographics && Object.keys(rd.patient_demographics).length > 0;
  const hasFacilities = Array.isArray(rd.facilities) && rd.facilities.length > 0;
  const hasBilling = Array.isArray(rd.billing_summary) && rd.billing_summary.length > 0;
  const hasImaging = Array.isArray(rd.diagnostics_imaging) && rd.diagnostics_imaging.length > 0;
  
  // OLD SCHEMA checks
  const hasProviders = Array.isArray(rd.providers_and_facilities) && rd.providers_and_facilities.length > 0;
  const hasTimeline = Array.isArray(rd.treatment_timeline) && rd.treatment_timeline.length > 0;
  const hasOldBilling = rd.billing_and_specials && (
    Array.isArray(rd.billing_and_specials.line_items) && rd.billing_and_specials.line_items.length > 0
  );
  const hasChronologyText = rd.chronology_text && rd.chronology_text.length > 0;
  
  // Return true if ANY field has data (either schema)
  return hasPatientDemo || hasFacilities || hasBilling || hasImaging || 
         hasProviders || hasTimeline || hasOldBilling || hasChronologyText;
};

const CaseDetailPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  
  const [caseData, setCaseData] = useState(null);
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resultsLoading, setResultsLoading] = useState(true);
  const [resultsError, setResultsError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [showUploadRequiredModal, setShowUploadRequiredModal] = useState(false);
  
  // Processing modal state
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('processing'); // 'processing' | 'complete' | 'error' | 'cancelled'
  const [processingProgress, setProcessingProgress] = useState(0);
  
  // Job polling state for concurrent processing support
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobStatusPollRef, setJobStatusPollRef] = useState(null); // Reference to stop polling on cancel
  
  // Reprocess status state
  const [reprocessStatus, setReprocessStatus] = useState(null);
  const [showSupportTicketModal, setShowSupportTicketModal] = useState(false);
  const [showReprocessConfirmModal, setShowReprocessConfirmModal] = useState(false);

  useEffect(() => {
    fetchCaseData();
    fetchFiles();
    fetchResults();
    fetchReprocessStatus();
  }, [caseId]);

  const fetchCaseData = async () => {
    try {
      const response = await axios.get(`${API}/cases/${caseId}`);
      setCaseData(response.data);
    } catch (error) {
      console.error("Error fetching case:", error);
      toast.error("Failed to load case");
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async () => {
    try {
      const response = await axios.get(`${API}/cases/${caseId}/files`);
      setFiles(response.data);
    } catch (error) {
      console.error("Error fetching files:", error);
    }
  };

  const fetchResults = async () => {
    setResultsLoading(true);
    setResultsError(null);
    try {
      const response = await axios.get(`${API}/cases/${caseId}/results`);
      if (response.data && hasValidResultData(response.data)) {
        setResults(response.data);
      } else {
        // Results exist but are empty
        setResults(null);
        if (response.data) {
          setResultsError("Summary data is empty. Please reprocess the case.");
        }
      }
    } catch (error) {
      // No results yet - that's okay for draft cases
      setResults(null);
      if (error.response?.status !== 404) {
        setResultsError("Failed to load summary. Please try again.");
      }
    } finally {
      setResultsLoading(false);
    }
  };

  const fetchReprocessStatus = async () => {
    try {
      const response = await axios.get(`${API}/cases/${caseId}/reprocess-status`);
      setReprocessStatus(response.data);
      console.log('[DEBUG] Reprocess status:', response.data);
    } catch (error) {
      console.error('[DEBUG] Error fetching reprocess status:', error);
    }
  };

  const handleProcess = async () => {
    // CRITICAL CHECK: Check if support is required (exceeded reprocess limit)
    if (reprocessStatus?.support_required) {
      setShowSupportTicketModal(true);
      return;
    }
    
    // Check if can reprocess
    if (results && reprocessStatus && !reprocessStatus.can_reprocess) {
      toast.error(`Maximum reprocess attempts (${reprocessStatus.max_reprocess_attempts}) exceeded. Please submit a support ticket.`);
      setShowSupportTicketModal(true);
      return;
    }

    // CRITICAL CHECK: Verify files are uploaded first
    if (!files || files.length === 0) {
      setShowUploadRequiredModal(true);
      return;
    }

    // Check if any files are still uploading or pending
    const pendingFiles = files.filter(f => 
      f.processing_status === 'pending' || 
      f.processing_status === 'uploading' || 
      f.processing_status === 'ocr_in_progress'
    );
    
    if (pendingFiles.length > 0) {
      setShowUploadRequiredModal(true);
      return;
    }

    // Check if user has credits
    if (!user || user.credit_balance < 1) {
      setShowBuyCredits(true);
      toast.error("Insufficient credits. Please purchase more credits.");
      return;
    }

    // Show confirmation modal
    setShowReprocessConfirmModal(true);
  };

  // Actual processing function called after confirmation
  const executeProcess = async () => {
    // Close confirmation modal
    setShowReprocessConfirmModal(false);

    // Show processing modal immediately
    setShowProcessingModal(true);
    setProcessingStatus('processing');
    setProcessingProgress(0);
    setProcessing(true);

    // Progress simulation will be replaced by actual job status updates
    let progressInterval = null;
    let jobStatusPoll = null;

    try {
      // Determine if this is a reprocess (has existing results)
      const isReprocess = results && results.result_data;
      
      // Start actual API call - this queues the job
      console.log('[DEBUG] Starting process request for case:', caseId, 'isReprocess:', isReprocess);
      const processUrl = isReprocess 
        ? `${API}/cases/${caseId}/process?reprocess=true`
        : `${API}/cases/${caseId}/process`;
      
      const processResponse = await axios.post(processUrl);
      const jobId = processResponse.data.job_id;
      const runNumber = processResponse.data.run_number;
      console.log('[DEBUG] Job queued with ID:', jobId, 'Run #:', runNumber);
      
      // Store current job ID for potential cancellation
      setCurrentJobId(jobId);
      
      // Poll for job status - NO TIMEOUT (continues indefinitely until complete/error/cancel)
      const pollJobStatus = async () => {
        return new Promise((resolve, reject) => {
          let pollCount = 0;
          const maxPolls = 9999; // Effectively no timeout - system should NEVER time out
          
          jobStatusPoll = setInterval(async () => {
            try {
              pollCount++;
              
              // Only log every 10th poll to reduce noise
              if (pollCount % 10 === 0) {
                console.log(`[DEBUG] Polling job status (${pollCount}):`, jobId);
              }
              
              const statusResponse = await axios.get(`${API}/jobs/${jobId}/status`);
              const { status, progress, error } = statusResponse.data;
              
              // Update progress from actual job status
              setProcessingProgress(progress || Math.min(pollCount * 0.5, 95));
              
              if (status === 'completed') {
                clearInterval(jobStatusPoll);
                setJobStatusPollRef(null);
                console.log('[DEBUG] Job completed successfully');
                resolve({ success: true });
              } else if (status === 'error') {
                clearInterval(jobStatusPoll);
                setJobStatusPollRef(null);
                console.log('[DEBUG] Job failed with error:', error);
                reject(new Error(error || 'Processing failed'));
              } else if (status === 'cancelled') {
                clearInterval(jobStatusPoll);
                setJobStatusPollRef(null);
                console.log('[DEBUG] Job was cancelled');
                reject(new Error('Request was cancelled'));
              } else if (pollCount >= maxPolls) {
                clearInterval(jobStatusPoll);
                setJobStatusPollRef(null);
                // Don't fail on timeout - job may still be running in background
                console.log('[DEBUG] Polling timeout - job may still be processing in background');
                reject(new Error('Processing is taking longer than expected. The job will continue in the background - check back shortly.'));
              }
            } catch (pollError) {
              console.error('[DEBUG] Error polling job status:', pollError);
              // Continue polling on transient errors
              if (pollCount >= maxPolls) {
                clearInterval(jobStatusPoll);
                setJobStatusPollRef(null);
                reject(new Error('Failed to check processing status'));
              }
            }
          }, 1000);
          
          // Store reference for cancellation
          setJobStatusPollRef(jobStatusPoll);
        });
      };
      
      // Wait for job to complete
      await pollJobStatus();
      
      // Job completed - now fetch results
      console.log('[DEBUG] Fetching results after completion');
      setProcessingProgress(100);
      
      // Fetch results with retry
      let retryCount = 0;
      const maxRetries = 3;
      let resultsLoaded = false;
      
      while (retryCount < maxRetries && !resultsLoaded) {
        try {
          const resultsResponse = await axios.get(`${API}/cases/${caseId}/results`);
          console.log('[DEBUG] Results response:', {
            hasData: !!resultsResponse.data,
            hasResultData: !!resultsResponse.data?.result_data,
            resultKeys: resultsResponse.data?.result_data ? Object.keys(resultsResponse.data.result_data) : []
          });
          
          if (resultsResponse.data && resultsResponse.data.result_data) {
            setResults(resultsResponse.data);
            setResultsError(null);
            resultsLoaded = true;
            console.log('[DEBUG] Results loaded successfully');
          } else {
            retryCount++;
            console.log(`[DEBUG] Results empty, retrying (${retryCount}/${maxRetries})`);
            await new Promise(r => setTimeout(r, 1000));
          }
        } catch (err) {
          retryCount++;
          console.log(`[DEBUG] Error fetching results, retrying (${retryCount}/${maxRetries}):`, err.message);
          await new Promise(r => setTimeout(r, 1000));
        }
      }
      
      if (!resultsLoaded) {
        console.error('[DEBUG] Failed to load results after retries');
        setResultsError("Results were generated but could not be loaded. Please refresh the page.");
      }
      
      await fetchCaseData();
      await fetchReprocessStatus(); // Refresh reprocess status
      await refreshUser();
      
      // Show completion state
      setProcessingStatus('complete');
      toast.success("Processing completed successfully");
      
    } catch (error) {
      console.error("[DEBUG] Error processing:", error);
      if (progressInterval) clearInterval(progressInterval);
      if (jobStatusPoll) clearInterval(jobStatusPoll);
      
      // Show error state
      setProcessingStatus('error');
      
      // Refresh reprocess status to check if limit exceeded
      await fetchReprocessStatus();
      
      if (error.response?.status === 402) {
        setShowBuyCredits(true);
        toast.error("Insufficient credits. Please purchase more credits.");
      } else if (error.response?.status === 400 && error.response?.data?.detail?.includes('maximum reprocess')) {
        // Max reprocess limit reached
        toast.error(error.response.data.detail);
        setShowSupportTicketModal(true);
      } else {
        toast.error(error.message || error.response?.data?.detail || "Processing failed");
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleViewResults = async () => {
    console.log('[DEBUG] handleViewResults called');
    setShowProcessingModal(false);
    setProcessingProgress(0);
    
    // Force refresh results to ensure we have the latest
    setResultsLoading(true);
    try {
      const response = await axios.get(`${API}/cases/${caseId}/results`);
      console.log('[DEBUG] handleViewResults - Fetched results:', {
        hasData: !!response.data,
        hasResultData: !!response.data?.result_data
      });
      
      if (response.data && response.data.result_data) {
        setResults(response.data);
        setResultsError(null);
      }
    } catch (error) {
      console.error('[DEBUG] handleViewResults - Error fetching results:', error);
    } finally {
      setResultsLoading(false);
    }
    
    // Wait a tick for state update, then scroll to results
    setTimeout(() => {
      const resultsElement = document.getElementById('results-tabs');
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        console.log('[DEBUG] Scrolled to results-tabs');
      } else {
        console.log('[DEBUG] results-tabs element not found - results may not be rendered');
        // If results element not found, try to re-fetch and show error
        toast.info("Loading results...");
      }
    }, 100);
  };

  const handleRetryProcessing = () => {
    setShowProcessingModal(false);
    setProcessingProgress(0);
    setProcessingStatus('processing');
    
    // Check if we can still retry
    if (reprocessStatus && !reprocessStatus.can_reprocess) {
      toast.error("Maximum reprocess attempts reached. Please submit a support ticket.");
      setShowSupportTicketModal(true);
      return;
    }
    
    // Retry after a short delay - go directly to executeProcess (skip confirmation)
    setTimeout(() => {
      executeProcess();
    }, 500);
  };

  // Handle cancel request - IMMEDIATE cancellation
  const handleCancelRequest = async () => {
    console.log('[DEBUG] handleCancelRequest called for job:', currentJobId);
    
    // IMMEDIATELY stop polling and update UI
    if (jobStatusPollRef) {
      clearInterval(jobStatusPollRef);
      setJobStatusPollRef(null);
    }
    
    // IMMEDIATELY update UI state to cancelled
    setProcessingStatus('cancelled');
    setProcessing(false);
    
    if (!currentJobId) {
      console.log('[DEBUG] No job ID to cancel - closing modal immediately');
      setShowProcessingModal(false);
      setProcessingProgress(0);
      return;
    }
    
    // Show toast immediately
    toast.info("Request cancelled. No credits were consumed.");
    
    try {
      // Call cancel API in background (don't wait for it to close modal)
      axios.post(`${API}/jobs/${currentJobId}/cancel`)
        .then(response => {
          console.log('[DEBUG] Cancel API response:', response.data);
          // Refresh case data in background
          fetchCaseData();
        })
        .catch(error => {
          console.error('[DEBUG] Cancel API error:', error);
        });
      
      // Close modal immediately (don't wait for API)
      setShowProcessingModal(false);
      setProcessingProgress(0);
      setCurrentJobId(null);
      
    } catch (error) {
      console.error('[DEBUG] Cancel error:', error);
      // Still close the modal immediately on error
      setShowProcessingModal(false);
      setProcessing(false);
      setCurrentJobId(null);
    }
  };

  const medicalFiles = files.filter(f => f.file_type === 'medical_records');
  const billFiles = files.filter(f => f.file_type === 'bills');

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto" style={{ color: '#FFFFFF' }} />
            <p className="mt-4" style={{ color: '#F5F7FA' }}>Loading case...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <BuyCreditsModal open={showBuyCredits} onClose={() => setShowBuyCredits(false)} />
      
      {/* Processing Modal */}
      <ProcessingModal
        isOpen={showProcessingModal}
        status={processingStatus}
        progress={processingProgress}
        onClose={() => setShowProcessingModal(false)}
        onViewResults={handleViewResults}
        onRetry={handleRetryProcessing}
        onCancel={handleCancelRequest}
        jobId={currentJobId}
      />
      
      {/* Reprocess Confirmation Modal */}
      <Dialog open={showReprocessConfirmModal} onOpenChange={setShowReprocessConfirmModal}>
        <DialogContent className="glass-card max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div 
                className="p-3 rounded-lg"
                style={{ backgroundColor: 'rgba(36, 84, 81, 0.1)' }}
              >
                <RefreshCw className="h-6 w-6" style={{ color: '#245451' }} />
              </div>
              <DialogTitle style={{ color: '#245451', fontSize: '1.5rem' }}>
                {results ? 'Reprocess Request?' : 'Process Request?'}
              </DialogTitle>
            </div>
            <DialogDescription style={{ color: '#64748B', fontSize: '1rem', lineHeight: '1.5' }}>
              {results ? (
                <>
                  This will re-run extraction using <strong>ALL</strong> current records and bills attached to this case.
                  <br /><br />
                  <span className="text-amber-600">
                    <strong>Note:</strong> This will use 1 credit and replace the current results.
                  </span>
                  {reprocessStatus && (
                    <div className="mt-3 p-2 bg-slate-100 rounded text-sm">
                      <strong>Documents to process:</strong> {files.length} files<br />
                      <strong>Reprocess attempt:</strong> {(reprocessStatus.reprocess_attempts || 0) + 1} of {reprocessStatus.max_reprocess_attempts}
                    </div>
                  )}
                </>
              ) : (
                <>
                  This will process all uploaded records and bills to generate a medical chronology and billing summary.
                  <br /><br />
                  <strong>This action will use 1 credit.</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowReprocessConfirmModal(false)}
              style={{ borderColor: '#E5E7EB', color: '#64748B' }}
              className="px-6"
            >
              Cancel
            </Button>
            <Button
              onClick={executeProcess}
              style={{ backgroundColor: '#245451', color: '#FFFFFF' }}
              className="px-6"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {results ? 'Reprocess' : 'Process'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Upload Required Modal */}
      <Dialog open={showUploadRequiredModal} onOpenChange={setShowUploadRequiredModal}>
        <DialogContent className="glass-card max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div 
                className="p-3 rounded-lg"
                style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
              >
                <Upload className="h-6 w-6" style={{ color: '#EF4444' }} />
              </div>
              <DialogTitle style={{ color: '#EF4444', fontSize: '1.5rem' }}>
                Upload Required
              </DialogTitle>
            </div>
            <DialogDescription style={{ color: '#64748B', fontSize: '1rem', lineHeight: '1.5' }}>
              Please click <strong>&quot;Upload All&quot;</strong> to upload your records and bills before generating a summary.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end mt-4">
            <Button
              onClick={() => setShowUploadRequiredModal(false)}
              style={{ backgroundColor: '#245451', color: '#FFFFFF' }}
              className="px-6"
            >
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="mb-4"
            style={{ color: '#FFFFFF' }}
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2" style={{ color: '#FFFFFF' }} data-testid="case-title">
                {caseData?.case_name}
              </h1>
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-4" style={{ color: '#F5F7FA' }}>
                  <span>Patient: {caseData?.patient_name}</span>
                  <span>•</span>
                  <span>Injury Date: {new Date(caseData?.date_of_injury).toLocaleDateString()}</span>
                  <span>•</span>
                  <span className={`px-2 py-1 rounded text-sm ${
                    caseData?.status === 'completed' ? 'bg-green-100 text-green-800' :
                    caseData?.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                    caseData?.status === 'support_required' ? 'bg-red-100 text-red-800' :
                    caseData?.status === 'error' ? 'bg-red-100 text-red-800' :
                    'bg-slate-100 text-slate-800'
                  }`}>
                    {caseData?.status === 'support_required' ? 'Support Required' : caseData?.status}
                  </span>
                </div>
                <div className="flex items-center space-x-2 text-sm" style={{ color: '#94a3b8' }}>
                  <span>Created: {caseData?.created_at ? new Date(caseData.created_at).toLocaleDateString() : 'N/A'}</span>
                  <span>•</span>
                  <span>{caseData?.created_at ? new Date(caseData.created_at).toLocaleTimeString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit',
                    hour12: true 
                  }) : 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Support Required Banner - Show when case exceeded reprocess limit */}
        {(reprocessStatus?.support_required || caseData?.status === 'support_required') && (
          <Card className="mb-6 border-red-300 bg-red-50">
            <CardContent className="py-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-red-100">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-red-800 mb-1">
                    Error — Support Required
                  </h3>
                  <p className="text-red-700 mb-3">
                    We attempted to process this request {reprocessStatus?.reprocess_attempts || 3} times and it still failed. 
                    Please submit a Support Ticket so our team can assist.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => navigate('/support/new', { 
                        state: { 
                          prefillData: {
                            subject: `Processing Failed - Case: ${caseData?.case_name || caseId}`,
                            category: 'Technical Issue',
                            priority: 'High',
                            caseId: caseId,
                            description: `Auto-generated: Case "${caseData?.case_name}" failed after ${reprocessStatus?.reprocess_attempts || 3} reprocess attempts.\n\nCase ID: ${caseId}\nPatient: ${caseData?.patient_name}\n\nPlease investigate and assist.`
                          }
                        }
                      })}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      <MessageSquare className="mr-2 h-4 w-4" />
                      Create Support Ticket
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/support')}
                      className="border-red-300 text-red-700 hover:bg-red-100"
                    >
                      <HelpCircle className="mr-2 h-4 w-4" />
                      View Existing Tickets
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reprocess Attempt Warning - Show when close to limit */}
        {reprocessStatus && reprocessStatus.reprocess_attempts >= 2 && reprocessStatus.can_reprocess && (
          <Card className="mb-4 border-amber-200 bg-amber-50">
            <CardContent className="py-3">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <p className="text-amber-800 text-sm">
                  <strong>Warning:</strong> You have used {reprocessStatus.reprocess_attempts} of {reprocessStatus.max_reprocess_attempts} reprocess attempts. 
                  After the {reprocessStatus.max_reprocess_attempts}rd attempt, you will need to contact support.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* File Upload Section - Show when no results OR when there's an error */}
        {((!results || !results.result_data) || resultsError) && !resultsLoading && !reprocessStatus?.support_required && (
          <div className="mb-8">
            {/* Show error banner if results exist but are empty or failed to load */}
            {resultsError && (
              <Card className="mb-4 border-amber-200 bg-amber-50">
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                    <div className="flex-1">
                      <p className="font-medium text-amber-800">{resultsError}</p>
                      <p className="text-sm text-amber-600 mt-1">
                        You can reprocess the documents to generate a new summary.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleProcess}
                      disabled={processing || files.length === 0}
                      className="border-amber-300 text-amber-700 hover:bg-amber-100"
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Reprocess
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            <Card className="glass-card shadow-sm">
              <CardContent>
                <UnifiedDocumentUploader
                  caseId={caseId}
                  onFilesUpdated={fetchFiles}
                />

                {files.length > 0 && (
                  <div className="mt-6 flex flex-col items-center">
                    <div className="mb-3 flex items-center space-x-2" style={{ color: '#406962' }}>
                      <CreditCard className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        You will use 1 credit to generate this summary
                      </span>
                    </div>
                    <Button
                      size="lg"
                      onClick={handleProcess}
                      disabled={processing}
                      className="bg-green-600 hover:bg-green-700 px-12 h-14 text-lg"
                      data-testid="generate-btn"
                    >
                      {processing ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <FileText className="mr-2 h-5 w-5" />
                          Generate Chronology & Specials
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Results Loading State */}
        {resultsLoading && !loading && (
          <div className="mb-8">
            <Card className="glass-card shadow-sm">
              <CardContent className="py-12">
                <div className="flex flex-col items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-teal-600 mb-4" />
                  <p className="text-slate-600 font-medium">Loading summary...</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Results Section - Show when we have results */}
        {results && results.result_data && (
          <div id="results-tabs" className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-slate-900">Analysis Results</h2>
                {reprocessStatus?.total_runs > 1 && (
                  <span className="text-sm text-slate-500">
                    (Run #{reprocessStatus?.current_run_number})
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {reprocessStatus?.reprocess_attempts > 0 && (
                  <span className="text-xs text-slate-500">
                    Reprocess attempts: {reprocessStatus?.reprocess_attempts}/{reprocessStatus?.max_reprocess_attempts}
                  </span>
                )}
                <Button
                  onClick={handleProcess}
                  disabled={processing || reprocessStatus?.support_required || !reprocessStatus?.can_reprocess}
                  variant="outline"
                  data-testid="reprocess-btn"
                  title={!reprocessStatus?.can_reprocess ? 'Maximum reprocess attempts reached' : 'Reprocess documents'}
                >
                  {processing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Reprocessing...
                    </>
                  ) : reprocessStatus?.support_required ? (
                    <>
                      <AlertTriangle className="mr-2 h-4 w-4" />
                      Support Required
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Reprocess ({reprocessStatus?.max_reprocess_attempts - reprocessStatus?.reprocess_attempts} left)
                    </>
                  )}
                </Button>
              </div>
            </div>

            <Tabs defaultValue="chronology" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6">
                <TabsTrigger value="chronology" data-testid="chronology-tab">
                  <FileText className="mr-2 h-4 w-4" />
                  Chronology
                </TabsTrigger>
                <TabsTrigger value="structured" data-testid="structured-tab">
                  <Database className="mr-2 h-4 w-4" />
                  Structured Data
                </TabsTrigger>
                <TabsTrigger value="specials" data-testid="specials-tab">
                  <DollarSign className="mr-2 h-4 w-4" />
                  Medical Specials
                </TabsTrigger>
              </TabsList>

              <TabsContent value="chronology">
                <RevampedChronologyTab results={results.result_data} />
              </TabsContent>

              <TabsContent value="structured">
                <StructuredDataTab data={results.result_data} />
              </TabsContent>

              <TabsContent value="specials">
                <SpecialsTab data={results.result_data} />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CaseDetailPage;
